import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, TablePagination } from '@mui/material';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { fetchCalendarificHolidays } from '../api/calendarific';

const API_URL = 'https://pdks.bytebridge.com.tr/api/official-holidays';

export default function OfficialHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [autoHolidays, setAutoHolidays] = useState(() => {
    // Son getirilen yıl ve tatil verisi localStorage'dan alınır
    const year = localStorage.getItem('lastAutoHolidayYear');
    if (year) {
      const cached = localStorage.getItem(`autoHolidays_${year}`);
      if (cached) return JSON.parse(cached);
    }
    return [];
  }); // Otomatik çekilen tatiller
  const [autoYear, setAutoYear] = useState(() => {
    const last = localStorage.getItem('lastAutoHolidayYear');
    return last ? parseInt(last, 10) : new Date().getFullYear();
  });
  const [autoFetched, setAutoFetched] = useState(() => {
    const year = localStorage.getItem('lastAutoHolidayYear');
    if (year) {
      const cached = localStorage.getItem(`autoHolidays_${year}`);
      return !!cached;
    }
    return false;
  }); // Tatiller getirildi mi?
  const [newHoliday, setNewHoliday] = useState({ name: '', startDate: '', endDate: '' });
  const [editId, setEditId] = useState(null);
  const [editHoliday, setEditHoliday] = useState({ name: '', startDate: '', endDate: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, holiday: null });
  const [editDialog, setEditDialog] = useState({ open: false, holiday: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [autoPage, setAutoPage] = useState(0);
  const [autoRowsPerPage, setAutoRowsPerPage] = useState(10);

  useEffect(() => {
    // Sadece bir kez/gün tetiklemek için localStorage kontrolü
    const today = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
    const sentKey = `holidayLogSent_${today}`;
    if (!localStorage.getItem(sentKey)) {
      axios.post('https://pdks.bytebridge.com.tr/api/auto-holiday-log')
        .then(res => {
          localStorage.setItem(sentKey, '1');
        })
        .catch(err => {
        });
    }
    fetchHolidays();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchHolidays(page, rowsPerPage);
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  // Calendarific ile otomatik tatil çekme
  const fetchAutoHolidays = async (year) => {
    localStorage.setItem('lastAutoHolidayYear', year);
    const cached = localStorage.getItem(`autoHolidays_${year}`);
    if (cached) {
      setAutoHolidays(JSON.parse(cached));
      setAutoFetched(true);
      return;
    }
    const result = await fetchCalendarificHolidays(year);
    const holidaysArr = result.map(h => ({
      localName: h.name,
      date: h.date.iso,
      type: h.type && h.type.length > 0 ? h.type[0] : '',
      description: h.description
    }));
    setAutoHolidays(holidaysArr);
    localStorage.setItem(`autoHolidays_${year}`, JSON.stringify(holidaysArr));
    setAutoFetched(true);
  };

  const fetchHolidays = async (currPage = page, currRowsPerPage = rowsPerPage) => {
    try {
      const res = await axios.get(`${API_URL}?page=${currPage + 1}&page_size=${currRowsPerPage}`);
      const holidaysData = res.data.data.map(h => ({
        id: h.id || h.ID,
        name: h.name || h.Name,
        startDate: h.startDate || h.StartDate || h.date || h.Date,
        endDate: h.endDate || h.EndDate || h.date || h.Date
      }));
      setHolidays(holidaysData);
      setTotalCount(res.data.total);
    } catch (err) {
      setHolidays([]);
      setTotalCount(0);
    }
  };

  const handleChange = (e) => {
    setNewHoliday({ ...newHoliday, [e.target.name]: e.target.value });
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.startDate || !newHoliday.endDate || !newHoliday.name) return;
    try {
      // Manuel eklemede de backend'in istediği formatı gönder
      const start = newHoliday.startDate.length === 10 ? newHoliday.startDate + 'T09:00' : newHoliday.startDate;
      const end = newHoliday.endDate.length === 10 ? newHoliday.endDate + 'T09:00' : newHoliday.endDate;
      await axios.post(API_URL, {
        name: newHoliday.name,
        startDate: start,
        endDate: end
      });
      setNewHoliday({ name: '', startDate: '', endDate: '' });
      fetchHolidays();
    } catch (err) {}
  };

  const handleDeleteHoliday = (id) => {
    const holiday = holidays.find(h => h.id === id);
    setDeleteDialog({ open: true, holiday });
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/${deleteDialog.holiday.id}`);
      setSnackbar({ open: true, message: 'Tatil başarıyla silindi', severity: 'success' });
      fetchHolidays();
    } catch (err) {
      let msg = 'Silme işlemi başarısız';
      if (err.response && err.response.data && err.response.data.error) {
        msg += ': ' + err.response.data.error;
      }
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
    setDeleteDialog({ open: false, holiday: null });
  };

  const handleCancelDelete = () => setDeleteDialog({ open: false, holiday: null });

  const handleEditClick = (holiday) => {
    setEditDialog({ open: true, holiday });
    setEditId(holiday.id);
    setEditHoliday({ name: holiday.name, startDate: holiday.startDate, endDate: holiday.endDate });
  };

  const handleConfirmEdit = async () => {
    try {
      const start = editHoliday.startDate.length === 10 ? editHoliday.startDate + 'T09:00' : editHoliday.startDate;
      const end = editHoliday.endDate.length === 10 ? editHoliday.endDate + 'T09:00' : editHoliday.endDate;
      await axios.put(`${API_URL}/${editId}`, {
        name: editHoliday.name,
        startDate: start,
        endDate: end
      });
      setSnackbar({ open: true, message: 'Tatil başarıyla güncellendi', severity: 'success' });
      setEditId(null);
      setEditHoliday({ name: '', startDate: '', endDate: '' });
      fetchHolidays();
    } catch (err) {
      let msg = 'Güncelleme başarısız';
      if (err.response && err.response.data && err.response.data.error) {
        msg += ': ' + err.response.data.error;
      }
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
    setEditDialog({ open: false, holiday: null });
  };

  const handleCancelEdit = () => {
    setEditDialog({ open: false, holiday: null });
    setEditId(null);
    setEditHoliday({ name: '', startDate: '', endDate: '' });
  };

  const handleEditInputChange = (e) => {
    setEditHoliday(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAutoChangePage = (event, newPage) => {
    setAutoPage(newPage);
  };

  const handleAutoChangeRowsPerPage = (event) => {
    setAutoRowsPerPage(parseInt(event.target.value, 10));
    setAutoPage(0);
  };

  // Türkçe tarih formatı için yardımcı fonksiyon (Türkiye saatiyle)
  function formatTurkishDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    // timeZone: 'Europe/Istanbul' ile Türkiye saatine göre göster
    return date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Europe/Istanbul' });
  }

  // Çok günlük tatil tarihlerini elle belirle
  function getMultiDayHolidayEnd(localName = '', date) {
    if (typeof localName !== 'string') localName = '';
    // Ramazan Bayramı: 2025-03-30 ~ 2025-04-01
    if (localName.includes('Ramazan')) {
      return '2025-04-01';
    }
    // Kurban Bayramı: 2025-06-15 ~ 2025-06-18
    if (localName.includes('Kurban')) {
      return '2025-06-18';
    }
    // Diğerleri tek gün
    return date;
  }

  // Filtreleme: Tüm tatilleri göster (Ramazan/Kurban çok gün ise sadece ilk günü değil, API'den gelen tüm günleri göster)
  function getDisplayedAutoHolidays(autoHolidays) {
    // Aynı tatilin (ör. Ramazan/Kurban) tüm günlerini göstermek için filtreleme yapma, hepsini sırayla göster
    return autoHolidays.map(h => {
      const startDate = h.start ? h.start : h.date;
      const endDate = getMultiDayHolidayEnd(h.localName, startDate);
      return {
        ...h,
        startDate,
        endDate
      };
    });
  }

  // Calendarific İngilizce isimleri Türkçeye çevir
  const holidayNameTr = (name) => {
    const map = {
      "New Year's Day": "Yılbaşı",
      "Ramadan Feast Eve": "Ramazan Bayramı Arifesi",
      "Ramadan Feast": "Ramazan Bayramı 1. Gün",
      "Ramadan Feast Holiday": "Ramazan Bayramı 2. Gün",
      "Ramadan Feast Holiday 2": "Ramazan Bayramı 3. Gün",
      "National Sovereignty and Children's Day": "23 Nisan Ulusal Egemenlik ve Çocuk Bayramı",
      "Labor and Solidarity Day": "1 Mayıs Emek ve Dayanışma Günü",
      "Commemoration of Atatürk, Youth and Sports Day": "19 Mayıs Atatürk'ü Anma, Gençlik ve Spor Bayramı",
      "Sacrifice Feast Eve": "Kurban Bayramı Arifesi",
      "Sacrifice Feast": "Kurban Bayramı 1. Gün",
      "Sacrifice Feast Holiday": "Kurban Bayramı 2. Gün",
      "Sacrifice Feast Holiday 2": "Kurban Bayramı 3. Gün",
      "Sacrifice Feast Holiday 3": "Kurban Bayramı 4. Gün",
      "Democracy and National Unity Day": "15 Temmuz Demokrasi ve Milli Birlik Günü",
      "Victory Day": "30 Ağustos Zafer Bayramı",
      "Republic Day Eve": "Cumhuriyet Bayramı Arifesi",
      "Republic Day": "29 Ekim Cumhuriyet Bayramı",
      "Ataturk Commemoration Day": "Atatürk'ü Anma Günü",
      "New Year's Eve": "Yılbaşı Arifesi"
    };
    return map[name] || name;
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Otomatik çekilen tatilleri database'e aktarma
  const handleBulkImport = async () => {
    if (!autoHolidays.length) return;
    try {
      // Otomatik tatiller ve mevcut manuel tatilleri birleştirerek kaydet
      const allHolidays = [
        ...getDisplayedAutoHolidays(autoHolidays).map(h => ({
          name: holidayNameTr(h.localName),
          startDate: h.startDate || h.date,
          endDate: h.endDate || h.date
        })),
        ...holidays.map(h => ({
          name: h.name,
          startDate: h.startDate,
          endDate: h.endDate
        }))
      ];
      for (const hol of allHolidays) {
        // Saat ekle (sabit 09:00)
        const start = hol.startDate.length === 10 ? hol.startDate + 'T09:00' : hol.startDate;
        const end = hol.endDate.length === 10 ? hol.endDate + 'T09:00' : hol.endDate;
        await axios.post(API_URL, { name: hol.name, startDate: start, endDate: end });
      }
      setSnackbar({ open: true, message: 'Tüm tatiller veritabanına kaydedildi.', severity: 'success' });
      fetchHolidays();
    } catch (err) {
      setSnackbar({ open: true, message: 'Aktarımda hata oluştu', severity: 'error' });
    }
  };

  const refetchCurrentPage = () => fetchHolidays(page, rowsPerPage);

  const paginatedAutoHolidays = getDisplayedAutoHolidays(autoHolidays).slice(
    autoPage * autoRowsPerPage,
    autoPage * autoRowsPerPage + autoRowsPerPage
  );

  return (
    <Box p={3}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Resmi tatil günlerini bu sayfadan görebilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="h5" gutterBottom>
          Resmi Tatiller
        </Typography>
      </div>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Tatil Adı"
          name="name"
          value={newHoliday.name}
          onChange={handleChange}
        />
        <TextField
          label="Başlangıç Tarihi"
          name="startDate"
          type="date"
          value={newHoliday.startDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bitiş Tarihi"
          name="endDate"
          type="date"
          value={newHoliday.endDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={handleAddHoliday}>Ekle</Button>
        <TextField
          label="Yıl"
          type="number"
          value={autoYear}
          onChange={e => setAutoYear(Number(e.target.value))}
          size="small"
          style={{ width: 90 }}
        />
        <Button variant="outlined" onClick={() => fetchAutoHolidays(autoYear)}>Resmi Tatilleri Getir</Button>
        <Button variant="contained" color="success" onClick={handleBulkImport} disabled={!autoHolidays.length}>
          Otomatik Tatilleri Kaydet
        </Button>
      </Box>
      {/* Otomatik çekilen tatiller tablosu */}
      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Türkiye Resmi Tatilleri (API ile otomatik):</Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tatil Adı</TableCell>
              <TableCell>Başlangıç Tarihi</TableCell>
              <TableCell>Bitiş Tarihi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!autoFetched ? (
              <TableRow><TableCell colSpan={3} align="center">Resmi tatilleri görmek için butona basın</TableCell></TableRow>
            ) : paginatedAutoHolidays.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center">Veri yok</TableCell></TableRow>
            ) : (
              paginatedAutoHolidays.map(h => (
                <TableRow key={h.startDate + h.localName}>
                  <TableCell>{holidayNameTr(h.localName)}</TableCell>
                  <TableCell>{formatTurkishDate(h.startDate)}</TableCell>
                  <TableCell>{formatTurkishDate(h.endDate)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {autoFetched && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={getDisplayedAutoHolidays(autoHolidays).length}
            rowsPerPage={autoRowsPerPage}
            page={autoPage}
            onPageChange={handleAutoChangePage}
            onRowsPerPageChange={handleAutoChangeRowsPerPage}
          />
        )}
      </TableContainer>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tatil Adı</TableCell>
              <TableCell>Başlangıç Tarihi</TableCell>
              <TableCell>Bitiş Tarihi</TableCell>
              <TableCell>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holidays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Kayıtlı resmi tatil yok</TableCell>
              </TableRow>
            ) : (
              holidays.map((holiday) => (
                <TableRow key={holiday.id}>
                  {editId === holiday.id ? (
                    <>
                      <TableCell>
                        <TextField name="name" value={editHoliday.name} onChange={handleEditInputChange} size="small" />
                      </TableCell>
                      <TableCell>
                        <TextField name="startDate" type="date" value={editHoliday.startDate} onChange={handleEditInputChange} size="small" InputLabelProps={{ shrink: true }} />
                      </TableCell>
                      <TableCell>
                        <TextField name="endDate" type="date" value={editHoliday.endDate} onChange={handleEditInputChange} size="small" InputLabelProps={{ shrink: true }} />
                      </TableCell>
                      <TableCell>
                        <Button color="success" onClick={handleConfirmEdit} size="small">Kaydet</Button>
                        <Button color="inherit" onClick={handleCancelEdit} size="small">Vazgeç</Button>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{holiday.name}</TableCell>
                      <TableCell>{formatTurkishDate(holiday.startDate)}</TableCell>
                      <TableCell>{formatTurkishDate(holiday.endDate)}</TableCell>
                      <TableCell>
                        <Button onClick={() => handleEditClick(holiday)} color="primary" size="small" startIcon={<EditIcon />} />
                        <Button color="error" onClick={() => handleDeleteHoliday(holiday.id)} size="small" startIcon={<DeleteIcon />} />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      {/* Silme Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCancelDelete}>
        <DialogTitle>Silme Onayı</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteDialog.holiday?.name}" tatilini silmek istediğinize emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Vazgeç</Button>
          <Button onClick={handleConfirmDelete} color="error">Sil</Button>
        </DialogActions>
      </Dialog>
      {/* Düzenleme Dialog */}
      <Dialog open={editDialog.open} onClose={handleCancelEdit}>
        <DialogTitle>Düzenleme Onayı</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{editDialog.holiday?.name}" tatilini düzenlemek istediğinize emin misiniz?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Tatil Adı"
            type="text"
            fullWidth
            variant="standard"
            value={editHoliday.name}
            onChange={handleEditInputChange}
          />
          <TextField
            margin="dense"
            name="startDate"
            label="Başlangıç Tarihi"
            type="date"
            fullWidth
            variant="standard"
            value={editHoliday.startDate}
            onChange={handleEditInputChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            name="endDate"
            label="Bitiş Tarihi"
            type="date"
            fullWidth
            variant="standard"
            value={editHoliday.endDate}
            onChange={handleEditInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Vazgeç</Button>
          <Button onClick={handleConfirmEdit} color="primary">Kaydet</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
